//@ts-ignore

/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { VApplication } from "../../application";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { getAgent } from "./agent";
import {
  TSupportedCountries,
  ICreatePolicyProps,
  IKYCSubmitProps,
} from "./components";
import { CALCULATE_PREMIUM } from "./graphql/calculatePremium.query";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";

const is = require("is_js");

const SERVICE_NAME = "FHPPolicyPageService";
const log: any = LoggerModule.getInstance();

const lookup = require("country-code-lookup");

export class FHPPolicyPageService {
  private static instance: FHPPolicyPageService;
  private vapp: VApplication;
  private kycFormData: any;
  private kycSubmissionData: any;
  private policyData: any;
  private termsData: any;
  private intendedPurpose: any;
  private policyOptions: any;
  private country: string;
  private policyHolder: any;

  private constructor(props: any = {}) {
    this.vapp = VApplication.getInstance();
    this.kycFormData = this.kycFormData;
    this.policyData = this.policyData;
    this.termsData = this.termsData;
    this.intendedPurpose = this.intendedPurpose;
    this.policyOptions = this.policyOptions;
    this.country = this.country;
    this.policyHolder = this?.policyHolder;
  }

  public static getInstance(props: any = {}): FHPPolicyPageService {
    if (!FHPPolicyPageService.instance) {
      FHPPolicyPageService.instance = new FHPPolicyPageService(props);
    }
    return FHPPolicyPageService.instance;
  }

  private setKycFormData(data: any) {
    this.kycFormData = data;
  }
  private setPolicyData(data: any) {
    this.policyData = data;
  }

  private setPolicyOptions(data: any) {
    this.policyOptions = data;
  }

  private setTermsData(data: any) {
    this.termsData = data;
  }
  private setIntendedPurpose(data: any) {
    this.intendedPurpose = data;
  }

  private resetPolicyData() {
    (this.kycFormData = {}),
      (this.policyData = null),
      (this.policyOptions = null),
      (this.termsData = {}),
      (this.intendedPurpose = {}),
      (this.country = ""),
      (this.policyHolder = {});
  }

  public getStaticPageContent() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = agent.getStaticPageContentSync({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public async getHeaderNavItems() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = await agent.getHeaderNavContent({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public async getKYCData() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();
    compProps = await agent.getKYCFormDataContent({});
    console.log("kyc data main", compProps)
    this.policyHolder = compProps?.policyHolder;
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public coverageToPremium({
    country,
    coverage,
    discount,
  }: {
    country: TSupportedCountries;
    coverage: any;
    discount: number;
  }) {
    const agent = getAgent();
    const pack = agent.coverageToPremiumSync({
      country,
      coverage: parseFloat(coverage),
      discount,
    });
    log.blue(SERVICE_NAME, "coverageToPremium", pack);
    return pack;
  }

  public premiumToCoverage({
    country,
    premium,
    discount
  }: {
    country: TSupportedCountries;
    premium: any;
    discount: number
  }) {
    log.blue(SERVICE_NAME, "premiumToCoverage::", country, premium);
    const agent = getAgent();
    const pack = agent.premiumToCoverageSync({ country, premium: parseFloat(premium), discount });
    log.blue(SERVICE_NAME, "premiumToCoverages", pack);
    return pack;
  }

  public async computeUpdatedPremium(args: any) {
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: CALCULATE_PREMIUM,
          variables: { input: args },
          fetchPolicy: "network-only",
        });
        return res?.data?.calculatePremium;
      }
    } catch (err: any) {
      console.log(`calculatePremium Error`, err);
    }
    return null;
  }

  public setKYCFormDate(formData: any) {
    this.setKycFormData(formData);
  }

  public setPolicyDetail(policyData: any) {
    this.setPolicyData(policyData);
  }

  public setTermsOfUseData(termsData: any) {
    this.setTermsData(termsData);
  }

  public SubmitIntendedPurpose(data: any) {
    this.setIntendedPurpose(data);
  }

  public onPolicyOptionChange(data: any) {
    this.setPolicyOptions(data);
  }

  public setCountry(data: any) {
    this.country = data;
  }

  public getPolicyPreviewData() {
    return {
      policyHolder:
        this.kycFormData?.firstName && this.kycFormData?.telephone
          ? {
            firstName: this.kycFormData?.firstName,
            lastName: this.kycFormData?.lastName,
            streetAddress: this.kycFormData?.streetAddress,
            city: this.kycFormData?.city,
            country: this.kycFormData?.country
              ? lookup.byIso(this.kycFormData?.country)?.country
              : "-",
            email: this.kycFormData?.email,
            telephone: this.kycFormData?.telephone,
          }
          : this.policyHolder,
      beneficiary: {
        firstName: this.kycFormData?.beneficiaryFirstName,
        lastName: this.kycFormData?.beneficiaryLastName,
        streetAddress: this.kycFormData?.beneficiaryStreetAddress,
        city: this.kycFormData?.beneficiaryCity,
        country: this.kycFormData?.beneficiaryCountry
          ? lookup.byIso(this.kycFormData?.beneficiaryCountry)?.country
          : "-",
        telephone: this.kycFormData?.beneficiaryTelephone,
        email: this.kycFormData?.beneficiaryEmail,
      },
      policyData: this.policyData,
      termsData: this.termsData,
    };
  }

  public getKYCFormData() {
    return this.kycFormData;
  }
  public getIntendedPurpose() {
    return this.intendedPurpose;
  }
  public getDefaultPolicy() {
    return this.policyOptions;
  }
  public resetFormData() {
    this.resetPolicyData();
  }

  public async getOfferData(args: any) {
    const agent = getAgent();
    return agent.getOfferDataContent(args);
  }

  public setKYCData(kycData: any) {
    this.kycSubmissionData = kycData;
  }

  public async submitKYC(args: IKYCSubmitProps) {
    log.info(`submit KYC:`, args);
    const agent = getAgent();
    const NewKYCdata = await agent.getKYCSubmitProps(args);
    console.log("data check", NewKYCdata);

    if (NewKYCdata?.submitKYCDetails) {
      this.kycSubmissionData = NewKYCdata.submitKYCDetails;
    }

    return NewKYCdata;
  }

  public async createPolicy(args: ICreatePolicyProps) {
    log.info(`create policy:`, this.policyData.coverage.raw, this.policyOptions, this.policyData.coverage);
    console.log("main store state", this.kycFormData);
    let _args = {
      beneficiaryFirstName: this.kycFormData?.beneficiaryFirstName,
      beneficiaryLastName: this.kycFormData?.beneficiaryLastName,
      beneficiaryStreetAddress: this.kycFormData?.beneficiaryStreetAddress,
      beneficiaryCity: this.kycFormData?.beneficiaryCity,
      beneficiaryCountry: this.kycFormData?.beneficiaryCountry,
      beneficiaryTelephone: this.kycFormData?.beneficiaryTelephone,
      beneficiaryEmail: this.kycFormData?.beneficiaryEmail,
      policyOption: this.policyData,
      policyCoverage: this.policyData.coverage.raw,
      intendedPurpose: this.intendedPurpose,
      termsData: this.termsData,
      country: this.country,
      kycData: this.kycSubmissionData,
      selectedDocs: args?.selectedDocs || args,
      kycSubmissionDetails: this.kycSubmissionData,
    };

    console.log("Selected Docs Data ==== ", args);
    console.log("KYC Submission Data ==== ", this.kycSubmissionData);

    const agent = getAgent();
    console.log(JSON.stringify(_args))
    return agent.getCreatePolicyProps(_args);
  }

  public async createRenewPolicy(args: any) {
    console.log("Renew Policy Args:", JSON.stringify(args));

    const _args = {
      beneficiaryFirstName: args.beneficiaryFirstName || "",
      beneficiaryLastName: args.beneficiaryLastName || "",
      beneficiaryStreetAddress: args.beneficiaryStreetAddress || "",
      beneficiaryCity: args.beneficiaryCity || "",
      beneficiaryCountry: args.beneficiaryCountry || "",
      beneficiaryTelephone: args.beneficiaryTelephone || "",
      beneficiaryEmail: args.beneficiaryEmail || "",
      proofIds: args?.proofIds || [],

      policyOption: args.policyOption,
      policyCoverage: args.policyCoverage || "",
      intendedPurpose: args.intendedPurpose || {},
      termsData: args.termsData || {},
      country: args.country || "Unknown",
    };

    const agent = getAgent();
    console.log(":wdhvcwhj", JSON.stringify(_args));

    return agent.getCreatePolicyProps(_args);
  }

  public async updateRenewPolicy(args: any) {
    const agent = getAgent();
    return agent.getUpdatePolicyProps(args?.policyId);
  }
}
